import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';

const TeluguYearContext = createContext();

const getCurrentYearId = () => {
  const today = new Date();
  return `${String(today.getMonth() + 1).padStart(2, '0')}${today.getFullYear()}`;
};

const getYearIdFromStartDate = (startDate) => {
  if (!startDate) {
    return '';
  }

  const [day, month, year] = startDate.split('-').map(Number);
  if (!day || !month || !year) {
    return '';
  }

  return `${String(month).padStart(2, '0')}${year}`;
};

// Telugu 60-year cycle names (Shashtiabda Samvatsaras)
const teluguYearNames = [
  'ప్రభవ', 'విభవ', 'శుక్ల', 'ప్రమోదూత', 'ప్రజోత్పత్తి', 'ఆంగీరస', 'శ్రీముఖ', 'భావ', 'యువ', 'ధాత',
  'ఈశ్వర', 'బహుధాన్య', 'ప్రమాథి', 'విక్రమ', 'విషు', 'చిత్రభాను', 'స్వభాను', 'తారణ', 'పార్థివ', 'వ్యయ',
  'సర్వజిత్', 'సర్వధారి', 'విరోధి', 'వికృతి', 'ఖర', 'నందన', 'విజయ', 'జయ', 'మన్మథ', 'దుర్ముఖి',
  'హేవళంబి', 'విళంబి', 'విఖారి', 'శార్వరి', 'ప్లవ', 'శుభకృత్', 'శోభకృత్', 'క్రోధి', 'విశ్వావసు', 'పరాభవ',
  'ప్లవంగ', 'కీలక', 'సౌమ్య', 'సాధారణ', 'విరోధకృత్', 'పరిధావి', 'ప్రమాదిచ', 'ఆనంద', 'రాక్షస', 'అనల',
  'పింగళ', 'కాళయుక్తి', 'సిద్ధార్థి', 'రౌద్ర', 'దుర్మతి', 'దుందుభి', 'రుధిరోద్గారి', 'రక్తాక్షి', 'క్రోధన', 'అక్షయ'
];

// Sanskrit 60-year cycle names (Shashtiabda Samvatsaras)
const sanskritYearNames = [
  'प्रभव', 'विभव', 'शुक्ल', 'प्रमोदूत', 'प्रजोत्पत्ति', 'आङ्गीरस', 'श्रीमुख', 'भाव', 'युव', 'धात',
  'ईश्वर', 'बहुधान्य', 'प्रमाथि', 'विक्रम', 'विषु', 'चित्रभानु', 'स्वभानु', 'तारण', 'पार्थिव', 'व्यय',
  'सर्वजित्', 'सर्वधारि', 'विरोधि', 'विकृति', 'खर', 'नन्दन', 'विजय', 'जय', 'मन्मथ', 'दुर्मुखि',
  'हेवलम्बि', 'विलम्बि', 'विकारि', 'शार्वरि', 'प्लव', 'शुभकृत्', 'शोभकृत्', 'क्रोधि', 'विश्वावसु', 'पराभव',
  'प्लवङ्ग', 'कीलक', 'सौम्य', 'साधारण', 'विरोधकृत्', 'परिधावि', 'प्रमादिच', 'आनन्द', 'राक्षस', 'अनल',
  'पिङ्गळ', 'कालयुक्ति', 'सिद्धार्थि', 'रौद्र', 'दुर्मति', 'दुन्दुभि', 'रुधिरोद्गारि', 'रक्ताक्षि', 'क्रोधन', 'अक्षय'
];

export const TeluguYearProvider = ({ children }) => {
  const [teluguYearTelugu, setTeluguYearTelugu] = useState('');
  const [teluguYearSanskrit, setTeluguYearSanskrit] = useState('');
  const [yearId, setYearId] = useState('');
  const [adminConfig, setAdminConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActiveConfig = async () => {
      try {
        const res = await api.get('/api/admin-config/active');
        const data = await res.json();
        
        if (res.ok && data.success && data.data) {
          const config = data.data;
          setAdminConfig(config);
          setYearId(config.year_id);
          
          // If year is directly set in config, use it
          if (config.year) {
            setTeluguYearTelugu(config.year);
            // Find the corresponding Sanskrit year
            const index = teluguYearNames.indexOf(config.year);
            if (index !== -1) {
              setTeluguYearSanskrit(sanskritYearNames[index]);
            }
          }
          
          // If start_date is provided, derive year from it
          if (config.start_date && !config.year) {
            const [day, month, year] = config.start_date.split('-').map(Number);
            
            // Base year for calculation (30-03-2025 = విశ్వావసు)
            const baseDate = new Date(2025, 2, 30); // March 30, 2025 (month is 0-indexed)
            const baseIndex = 38; // విశ్వావసు is at index 38
            
            const configDate = new Date(year, month - 1, day);
            
            // Calculate year difference
            const yearDiff = configDate.getFullYear() - baseDate.getFullYear();
            
            // Adjust for month/day difference
            let adjustedYearDiff = yearDiff;
            if (configDate.getMonth() < baseDate.getMonth() || 
                (configDate.getMonth() === baseDate.getMonth() && configDate.getDate() < baseDate.getDate())) {
              adjustedYearDiff -= 1;
            }
            
            const index = (baseIndex + adjustedYearDiff) % 60;
            const finalIndex = index < 0 ? index + 60 : index;
            
            setTeluguYearTelugu(teluguYearNames[finalIndex]);
            setTeluguYearSanskrit(sanskritYearNames[finalIndex]);
          }
        } else {
          // Fallback to current date-based calculation if no active config
          setYearId(getCurrentYearId());
          const today = new Date();
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth(); // 0-11
          const currentDay = today.getDate();
          
          // Telugu year changes on Ugadi (usually late March/early April)
          // For simplicity, using March 30 as the threshold
          let teluguYear = currentYear;
          if (currentMonth < 2 || (currentMonth === 2 && currentDay < 30)) {
            teluguYear = currentYear - 1;
          }
          
          // Base year for calculation (2025 starting March 30 = విశ్వావసు)
          const baseYear = 2025;
          const baseIndex = 38; // విశ్వావసు
          
          const yearDiff = teluguYear - baseYear;
          const index = (baseIndex + yearDiff) % 60;
          const finalIndex = index < 0 ? index + 60 : index;
          
          setTeluguYearTelugu(teluguYearNames[finalIndex]);
          setTeluguYearSanskrit(sanskritYearNames[finalIndex]);
        }
      } catch (error) {
        console.error('Error fetching active config:', error);
        // Fallback to current year calculation
        setYearId(getCurrentYearId());
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const currentDay = today.getDate();
        
        let teluguYear = currentYear;
        if (currentMonth < 2 || (currentMonth === 2 && currentDay < 30)) {
          teluguYear = currentYear - 1;
        }
        
        const baseYear = 2025;
        const baseIndex = 38;
        const yearDiff = teluguYear - baseYear;
        const index = (baseIndex + yearDiff) % 60;
        const finalIndex = index < 0 ? index + 60 : index;
        
        setTeluguYearTelugu(teluguYearNames[finalIndex]);
        setTeluguYearSanskrit(sanskritYearNames[finalIndex]);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveConfig();
  }, []);

  return (
    <TeluguYearContext.Provider value={{ 
      teluguYearTelugu, 
      teluguYearSanskrit, 
      yearId,
      adminConfig,
      loading 
    }}>
      {children}
    </TeluguYearContext.Provider>
  );
};

export const useTeluguYear = () => {
  const context = useContext(TeluguYearContext);
  if (!context) {
    throw new Error('useTeluguYear must be used within a TeluguYearProvider');
  }
  return context;
};
